import { ArrowLeft, RotateCcw, Sparkles } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/shared/components/ui/alert-dialog';
import { TwoStepProgress } from './TwoStepProgress';
import { ChatThread } from './ChatThread';
import { ChatComposer } from './ChatComposer';
import { useNavigatorChat } from '../hooks/useNavigatorChat';

export const NavigatorChat = () => {
  const {
    isFpMode,
    messages,
    input,
    setInput,
    isTyping,
    isClassificationConfirmed,
    isResumed,
    isCompleted,
    startNewChat,
    scrollRef,
    textareaRef,
    recaptchaElement,
    handleSend,
    goToGenerating,
    isAuthenticated,
    goToDashboard,
    goToNavigatorDashboard,
  } = useNavigatorChat();

  return (
    <div className="min-h-screen bg-background">
      {recaptchaElement}
      <TwoStepProgress
        currentStep={isClassificationConfirmed ? 'results' : 'intake'}
        mode={isFpMode ? 'fp' : 'care-seeker'}
        leftAction={
          isAuthenticated && !isFpMode ? (
            <button
              type="button"
              onClick={goToNavigatorDashboard}
              aria-label="See my previous Guides"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border-subtle bg-card-subtle px-3 py-2 text-sm font-medium text-text-primary transition-colors duration-150 ease-out hover:bg-sage hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              <span className="hidden md:inline">See my previous Guides</span>
            </button>
          ) : undefined
        }
      />

      <div className="container mx-auto px-4 py-8">
        <div className="mx-auto w-full max-w-3xl">
          {isFpMode && (
            <button
              type="button"
              onClick={goToDashboard}
              className="mb-6 inline-flex items-center gap-1 text-sm text-text-secondary transition-colors duration-150 ease-out hover:text-text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              Back to Care Guides
            </button>
          )}

          <div className="mb-2 flex items-center justify-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-sage text-white">
              <Sparkles className="h-4 w-4" aria-hidden />
            </span>
            <h1 className="font-termina font-medium text-2xl text-text-primary">bQuest Navigator</h1>
          </div>
          <p className="mb-6 text-center text-sm text-text-secondary">
            {isFpMode ? 'AI-powered care planning for your client' : 'AI-powered care planning assistant'}
          </p>

          {isResumed && (
            <div className="mb-4 flex justify-center">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-full border border-border-subtle bg-card-base px-4 py-1.5 text-sm font-medium text-text-secondary transition-colors duration-150 ease-out hover:border-sage hover:text-text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
                  >
                    <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                    Start a new chat
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Start a new chat?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This clears your current conversation and starts a fresh Navigator chat from
                      the beginning. Your in-progress answers won&apos;t be saved.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="border-0 bg-[#026857] text-white hover:bg-[#025246] focus:ring-[#026857]">
                      Keep my chat
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={startNewChat}
                      className="bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-400"
                    >
                      Yes, start new
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}

          <div className="rounded-2xl border border-border-subtle bg-card-base shadow-[0_1px_2px_rgba(20,30,28,0.04),0_4px_14px_rgba(20,30,28,0.04)]">
            <ChatThread
              ref={scrollRef}
              messages={messages}
              isTyping={isTyping}
              onQuickReply={(reply) => void handleSend(reply)}
              onGenerateCareGuide={goToGenerating}
            />
            <ChatComposer
              ref={textareaRef}
              input={input}
              isTyping={isTyping}
              disabled={isCompleted}
              onInputChange={setInput}
              onSend={() => void handleSend()}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
